
import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useParams, useLocation, Link, NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faEnvelope, faLocationPin, faPencil } from "@fortawesome/free-solid-svg-icons";
import { Alert, Breadcrumb } from "@boozallen-uip/uswds-react-core";
import PropTypes from "prop-types";
import { ProductContext } from './ProductContext';
import PageHeader from "../../components/PageHeader/PageHeader.component";
import ExternalLinkIcon from "../../components/ExternalLinkIcon.component";
import { SectionContainer } from "../shared.styled";
import "./ProductDetailListContainer.scss";
import useProduct from "../../hooks/useProduct.hook";
import useEntity from "../../hooks/useEntity.hook";
import useHyperlink, { getAcrTypeLabel } from "../../hooks/useHyperlink.hook";
import useReport from "../../hooks/useReport.hook";
import useCatalogApi from "../../hooks/useCatalogApi.hook";
import loadProductAcrExportModel, { findCompletedReport } from "../../components/acr-editor/utils/acrExport/loadProductAcrExportModel";
import AcrDownloadMenu from "../../components/acr-editor/components/AcrDownloadMenu";
import buildAcrHtmlString from "../../components/acr-editor/utils/acrExport/buildAcrHtmlString";
import canDownloadProductAcr from "./utils/canDownloadProductAcr";
import inSiteAcrAddedOn, { inSiteAcrUpdatedOn } from "./utils/inSiteAcrAddedOn";
import validateHyperlink, { formatDisplayDate, formatUTCDisplayDate } from "../../utlities/common.funcs";

import { ACCESS_LEVEL_ADMIN, ACCESS_LEVEL_USER, BASE_TITLE, FOR_BUYERS_URL, PRODUCT_EDIT_URL, HOME_URL, PRODUCT_DETAIL_URL } from "../../utlities/core.constants";
import { UserContext } from "../UserContext";

const ACR_REPO_ADMIN_EMAIL = "acr.repository@gsa.gov";

/** Join a product POC's address parts, dropping any that are missing so the UI never renders "null". */
function formatProductPocAddress(poc) {
  const streetCityParts = [poc?.street1, poc?.street2, poc?.city]
    .map((part) => (part ? String(part).trim() : ""))
    .filter(Boolean);
  const stateZip = [poc?.state, poc?.zipCode]
    .map((part) => (part ? String(part).trim() : ""))
    .filter(Boolean)
    .join(" ");
  if (stateZip) {
    streetCityParts.push(stateZip);
  }
  return streetCityParts.join(", ");
}

function EditButton({ productId }) {
  return <Link className="no-underline" to={`${PRODUCT_EDIT_URL}/${productId}`}>
    <FontAwesomeIcon className="text-xl" icon={faPencil} />&nbsp;
    <span className="underline text-sm">Edit page</span>
  </Link>
}

EditButton.propTypes = {
  productId: PropTypes.string.isRequired,
}

function AcrDetailField({ title, children }) {
  return (
    <div className="border-0 border-t border-solid border-primary-lighter py-3 first:border-t-0 first:pt-0 last:pb-0">
      <dt className="font-bold text-primary-darker mb-1">{title}</dt>
      <dd className="m-0 break-words">{children}</dd>
    </div>
  );
}

AcrDetailField.propTypes = {
  title: PropTypes.node.isRequired,
  children: PropTypes.node,
};

AcrDetailField.defaultProps = {
  children: null,
};

/**
 * In-page ACR viewer. Renders the same self-contained HTML we let users download, inside a
 * sandboxed, fixed-height iframe so the full ACR is viewable immediately without the page itself
 * growing unbounded. Scripts stay disabled (sandbox); allow-popups lets the ACR's guidance links
 * open in a new tab.
 */
function AcrReportViewer({ productName, exportState, fallback }) {
  const { status, evaluation, catalog } = exportState;

  const htmlString = useMemo(() => {
    if (status !== "ready" || !evaluation || !catalog) {
      return "";
    }
    try {
      return buildAcrHtmlString(evaluation, catalog);
    } catch {
      return "";
    }
  }, [status, evaluation, catalog]);

  if (status === "idle" || status === "loading") {
    return (
      <p className="m-0 text-sm" role="status" aria-live="polite">
        Loading the Accessibility Conformance Report…
      </p>
    );
  }

  if (!htmlString) {
    return fallback;
  }

  return (
    <iframe
      title={`${productName || "Product"} Accessibility Conformance Report`}
      srcDoc={htmlString}
      sandbox="allow-popups allow-popups-to-escape-sandbox"
      className="w-full h-[70vh] min-h-[28rem] rounded-sm border border-solid border-gray-light bg-white"
    />
  );
}

AcrReportViewer.propTypes = {
  productName: PropTypes.string,
  exportState: PropTypes.shape({
    status: PropTypes.string,
    evaluation: PropTypes.object,
    catalog: PropTypes.object,
  }).isRequired,
  fallback: PropTypes.node,
};

AcrReportViewer.defaultProps = {
  productName: "",
  fallback: null,
};

// Kept for backward compatibility with existing imports; the gate now lives in a shared util so the
// product listing page can apply the exact same download rules.
export const canDownloadProductAcrFromDetailPage = canDownloadProductAcr;

function ProductDetailListContainerFC() {

  // eslint-disable-next-line no-unused-vars
  const [category, setCategory] = useState(
    {
      "id": "",
      "name": "",
      "version": "",
      "description": "",
      "category": "",
      "vendorId": "",
      "isActive": false,
      "hyperlinkReferenceIds": [],
      "createdDate": "",
      "lastModifiedDate": "",
      "createdBy": "",
      "lastModifiedBy": "",
      "lastActivatedBy": null,
      "lastActivatedDate": "",
      "lastDeactivatedBy": null,
      "lastDeactivatedDate": ""
    }
  )
  const [product, setProduct] = useState(null);

  const [vendor, setVendor] = useState({
    "id": "",
    "name": "",
    "primaryPOCName": "",
    "primaryPOCEmail": "",
    "isActive": false,
    "productReferenceIds": [],
    "createdDate": "",
    "lastModifiedDate": "",
    "createdBy": "",
    "modifiedBy": "",
    "lastActivatedBy": null,
    "lastActivatedDate": "",
    "lastDeactivatedBy": null,
    "lastDeactivatedDate": ""
  })
  const location = useLocation();

  const [hyperlink, setHyperlink] = useState();
  const [hyperlinks, setHyperlinks] = useState([]);
  const [acrLinkUploadFailed, setAcrLinkUploadFailed] = useState(
    Boolean(location.state?.acrLinkUploadFailed)
  );
  const [pocSaveFailed, setPocSaveFailed] = useState(
    Boolean(location.state?.pocSaveFailed)
  );
  const [productUpdateFailed, setProductUpdateFailed] = useState(
    Boolean(location.state?.productUpdateFailed)
  );
  // const [filteredHyperlinks, setFilteredHyperlinks] = useState([]);

  const { categories } = useContext(ProductContext);
  const {
    isVendorUser,
    isAgencyUser,
    isSuperUser,
    checkEntityAccessLevel,
    user,
    userEntity,
    pocAddressFieldsEmpty,
  } = useContext(UserContext);
  const { productId } = useParams();
  const { fetchProduct } = useProduct();
  const { getVendor } = useEntity();
  const { getHyperlinkByProductId, getHyperlinkById } = useHyperlink();
  const { fetchReportsByProduct } = useReport();
  const { getCompleteCatalogById, getConformanceLevels } = useCatalogApi();

  const [publicAcrExport, setPublicAcrExport] = useState({
    status: "idle",
    evaluation: null,
    catalog: null,
  });
  // The completed in-site report this viewer may see, once the lookup finishes. Null means
  // there is no in-site ACR, so the sidebar dates fall back to the external hyperlink.
  const [inSiteReportLookup, setInSiteReportLookup] = useState({
    resolved: false,
    report: null,
  });

  const linkClasses = "rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-cool";

  useEffect(() => {
    if (location.state?.acrLinkUploadFailed) {
      setAcrLinkUploadFailed(true);
    }
    if (location.state?.pocSaveFailed) {
      setPocSaveFailed(true);
    }
    if (location.state?.productUpdateFailed) {
      setProductUpdateFailed(true);
    }
  }, [location.state]);

  const getProductAcrLinkContent = useCallback(() => {
    if (!hyperlinks.length) {
      return "Not Provided";
    }

    return (
      <div className="flex flex-col gap-1">
        {hyperlinks.map((item) => {
          if (item?.requestRequired) {
            return (
              <div key={item?.id || `request-required-${item?.acrType || ""}`}>
                Contact for ACR
              </div>
            );
          }

          if (!item?.url) {
            return (
              <div key={item?.id || `missing-url-${item?.acrType || ""}`}>
                Not Provided
              </div>
            );
          }

          const displayUrl = validateHyperlink(item.url);
          return (
            <div key={item?.id || displayUrl}>
              <a target="_blank" rel="noreferrer" href={displayUrl}>
                View ACR Externally
                <span className="usa-sr-only"> (opens in new window)</span>
                <ExternalLinkIcon />
              </a>
            </div>
          );
        })}
      </div>
    );
  }, [hyperlinks]);



  const displayUserType = useCallback(() => {
    if (!product?.lastModifiedByType) {
      return null;
    }
    return (<span className='bg-secondary-lighter text-secondary-darker py-1 px-2 uppercase text-xs'>{product?.lastModifiedByType}</span>);
  }, [product]);

  const getVendorLink = useCallback(() => {
    const currentVendorName = userEntity?.entity?.name || vendor?.name || "N/A";
    if (isVendorUser && checkEntityAccessLevel(ACCESS_LEVEL_ADMIN) && product?.vendorId === user?.vendorId) {
      return <span>{currentVendorName}</span>;
    }
    if (product?.agencyId) {
      // Agency-owned products have no vendor page; show the owning agency name without a link.
      return <span>{product?.agencyName || vendor?.name || "N/A"}</span>;
    }
    if (vendor && vendor.name && vendor.id) {
      return <NavLink to={`/vendor/${vendor?.id}`} className={linkClasses}>{vendor?.name}</NavLink>;
    }

    return "N/A"

  }, [isVendorUser, checkEntityAccessLevel, userEntity, product, user, vendor, linkClasses])



  useEffect(() => {
    if (!productId) {
      return;
    }

    (async () => {
      const productResponse = await fetchProduct(productId);
      if (!productResponse) {
        return;
      }

      setProduct(productResponse);

      const fetchedCategory = categories.find((categoryFromContext) => productResponse.category === categoryFromContext.name);
      setCategory(fetchedCategory);

      const userVendorId = String(userEntity?.entity?.id || user?.vendorId || "");
      const productVendorId = String(productResponse?.vendorId || "");

      const userAgencyId = String(userEntity?.entity?.id || user?.agencyId || "");
      const productAgencyId = String(productResponse?.agencyId || "");

      if (isVendorUser && userVendorId && productVendorId === userVendorId && userEntity?.entity) {
        setVendor(userEntity.entity);
      } else if (isAgencyUser && userAgencyId && productAgencyId === userAgencyId && userEntity?.entity) {
        setVendor(userEntity.entity);
      } else if (productResponse?.vendor?.id && productResponse?.vendor?.name) {
        setVendor(productResponse.vendor);
      } else if (productResponse?.vendorId) {
        const vendorResponse = await getVendor(productResponse.vendorId);
        setVendor(vendorResponse);
      } else {
        // Agency-owned product with no vendor: surface the owning agency name for display.
        setVendor((prev) => ({ ...prev, name: productResponse?.agencyName || "" }));
      }
      let resolvedHyperlinks = Array.isArray(productResponse?.hyperlinks)
        ? productResponse.hyperlinks.filter((item) => item?.isActive !== false)
        : [];

      if (!resolvedHyperlinks.length) {
        const hyperlinkReferenceIds = Array.isArray(productResponse?.hyperlinkReferenceIds)
          ? productResponse.hyperlinkReferenceIds.filter(Boolean)
          : [];
        if (hyperlinkReferenceIds.length) {
          const hyperlinkResults = await Promise.allSettled(
            hyperlinkReferenceIds.map((id) => getHyperlinkById(id))
          );
          resolvedHyperlinks = hyperlinkResults
            .filter((result) => result.status === "fulfilled")
            .map((result) => result.value)
            .filter((item) => item?.isActive !== false);
        }
      }

      if (!resolvedHyperlinks.length && productResponse?.isPublished === true) {
        const hyperlinkResponse = await getHyperlinkByProductId(productId);
        if (hyperlinkResponse && hyperlinkResponse.isActive !== false) {
          resolvedHyperlinks = [hyperlinkResponse];
        }
      }

      setHyperlinks(resolvedHyperlinks);
      setHyperlink(resolvedHyperlinks[0] ?? null);

    })();

  }, [productId, categories, fetchProduct, getVendor, getHyperlinkByProductId, getHyperlinkById, isVendorUser, isAgencyUser, user?.vendorId, user?.agencyId, userEntity]);

  useEffect(() => {
    if (!productId || !product || String(product.id) !== String(productId)) {
      return undefined;
    }
    let cancelled = false;
    setPublicAcrExport({ status: "loading", evaluation: null, catalog: null });
    setInSiteReportLookup({ resolved: false, report: null });

    (async () => {
      try {
        const reports = await fetchReportsByProduct(productId);
        if (cancelled) return;
        const report = findCompletedReport(reports);
        const canDownload = Boolean(report) && canDownloadProductAcr({
          isSuperUser,
          isVendorUser,
          isAgencyUser,
          userVendorId: user?.vendorId,
          productVendorId: product?.vendorId,
          userAgencyId: user?.agencyId,
          productAgencyId: product?.agencyId,
          reportIsPublic: report?.isPublic,
        });
        if (!canDownload) {
          if (!cancelled) {
            setInSiteReportLookup({ resolved: true, report: null });
            setPublicAcrExport({ status: "unavailable", evaluation: null, catalog: null });
          }
          return;
        }
        if (!cancelled) {
          setInSiteReportLookup({ resolved: true, report });
        }
        const result = await loadProductAcrExportModel({
          product,
          vendor,
          report,
          fetchReportsByProduct,
          getCompleteCatalogById,
          getConformanceLevels,
        });
        if (cancelled) return;
        if (!result.ok) {
          setPublicAcrExport({ status: "unavailable", evaluation: null, catalog: null });
          return;
        }
        setPublicAcrExport({
          status: "ready",
          evaluation: result.evaluation,
          catalog: result.catalog,
        });
      } catch {
        if (!cancelled) {
          setInSiteReportLookup({ resolved: true, report: null });
          setPublicAcrExport({ status: "unavailable", evaluation: null, catalog: null });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    productId,
    product,
    vendor,
    user,
    isSuperUser,
    isAgencyUser,
    isVendorUser,
    fetchReportsByProduct,
    getCompleteCatalogById,
    getConformanceLevels,
  ]);

  document.title = product?.name
    ? `${BASE_TITLE} - ${product.name}`
    : `${BASE_TITLE} - Product Details`;

  if (!product) {
    return null;
  }

  let partialSubmitMessage = null;
  if (acrLinkUploadFailed || pocSaveFailed || productUpdateFailed) {
    if (productUpdateFailed) {
      partialSubmitMessage = "There was an error completing product submission. Your product was saved as a draft. Edit this product and try submitting again.";
    } else if (acrLinkUploadFailed && pocSaveFailed) {
      partialSubmitMessage = "There was an error saving the ACR link and primary contact details. Your product was saved as a draft. Edit this product to try again.";
    } else if (acrLinkUploadFailed) {
      partialSubmitMessage = "There was an error saving the ACR link. Your product was saved as a draft. Edit this product to try adding the ACR link again.";
    } else {
      partialSubmitMessage = "There was an error saving the primary contact details. Your product was saved as a draft. Edit this product to try adding the contact again.";
    }
  }

  const canEdit = checkEntityAccessLevel([ACCESS_LEVEL_ADMIN, ACCESS_LEVEL_USER])
    && (
      (isVendorUser && product?.vendorId === user?.vendorId)
      || (isAgencyUser && product?.agencyId === user?.agencyId)
    );

  const acrExportReady = publicAcrExport.status === "ready"
    && Boolean(publicAcrExport.evaluation)
    && Boolean(publicAcrExport.catalog);

  // An in-site ACR (the uploaded report) owns these dates. The external hyperlink is only
  // used when that report is not present for this viewer.
  const dateAcrAdded = inSiteReportLookup.resolved
    ? (formatUTCDisplayDate(
      inSiteReportLookup.report
        ? inSiteAcrAddedOn(inSiteReportLookup.report)
        : hyperlink?.authoredDate
    ) || "Not Provided")
    : "";
  const dateAcrUpdated = inSiteReportLookup.resolved
    ? (formatUTCDisplayDate(
      inSiteReportLookup.report
        ? inSiteAcrUpdatedOn(inSiteReportLookup.report)
        : hyperlink?.lastModifiedDate
    ) || "Not Provided")
    : "";

  const versionLabel = product?.version ? `Version ${product.version}` : "Version not provided";
  const owningOrgName = product?.agencyName || vendor?.name || product?.vendor?.name || "";

  // When there's no in-site ACR to render inline, fall back to the external ACR hyperlink if one
  // exists; otherwise tell the user no ACR is available yet and how to request it.
  const hasAcrHyperlink = hyperlinks.some((item) => item?.url);
  // The uploaded ACR is already in the viewer on the left. Drop the sidebar Product ACR
  // and ACR Type rows when there is no external URL to show beside it.
  const showExternalAcrFields = !(acrExportReady && !hasAcrHyperlink);
  const acrViewerFallback = (
    <div className="rounded-sm border border-dashed border-gray-light p-6 text-sm">
      {hasAcrHyperlink ? (
        <>
          <p className="mt-0 mb-2">
            An in-page preview isn&apos;t available for this ACR because it has not been uploaded to
            ACR Repository. Use the link below provided by {owningOrgName || "the vendor"} to find
            the ACR:
          </p>
          {getProductAcrLinkContent()}
        </>
      ) : (
        <p className="m-0">
          No ACR is available for this product yet. Reach out to {owningOrgName || "the organization"} or the ACR admin team at{" "}
          <a href={`mailto:${ACR_REPO_ADMIN_EMAIL}`}>{ACR_REPO_ADMIN_EMAIL}</a> to request the ACR.
        </p>
      )}
    </div>
  );

  // Get In Touch shows the product's own POC (product.productPoc), a different source than the
  // org page's Get In Touch (which uses the org POC). Skip any field that's missing so we never
  // render "null"; if none are present, fall back to the admin contact message.
  const productPoc = product?.productPoc;
  const productPocPhone = productPoc?.phoneNumber ? String(productPoc.phoneNumber).trim() : "";
  const productPocEmail = productPoc?.email ? String(productPoc.email).trim() : "";
  const productPocAddress = pocAddressFieldsEmpty(productPoc) ? "" : formatProductPocAddress(productPoc);
  const hasAnyProductPoc = Boolean(productPocPhone || productPocEmail || productPocAddress);

  return (
    <>
      <PageHeader imageUrl="/page-banners/account_hero_2x.webp" backgroundSize='2800px 391px' className='2xl:!bg-cover'>
        Product Details
      </PageHeader>
      <SectionContainer className="pt-0 mb-10 text-primary-darker font-normal font-sans">
        <div className="mt-3 mb-4">
          <div className="mb-4">
            <Breadcrumb links={[{ "href": HOME_URL, "text": "Home" }, { "href": FOR_BUYERS_URL, "text": "Repository" }, { "href": PRODUCT_DETAIL_URL, "text": "Product Details" }]} />
          </div>
          <h1 className="text-primary text-2xl font-normal m-0">Product Details</h1>
        </div>

        {partialSubmitMessage ? (
          <Alert
            type="error"
            heading="Product Submission Incomplete"
            onClose={() => {
              setAcrLinkUploadFailed(false);
              setPocSaveFailed(false);
              setProductUpdateFailed(false);
            }}
          >
            {partialSubmitMessage}
          </Alert>
        ) : null}

        <div className="flex flex-wrap items-end justify-between gap-2 mt-2">
          <h2 className="m-0 font-normal text-primary">{`${product?.name} Overview`}</h2>
          {canEdit ? <EditButton productId={product.id} /> : null}
        </div>

        <div className="grid grid-cols-12 gap-8 mt-6">
          <div className="col-span-12 lg:col-span-8">
            <p className="text-primary-darker font-semibold mt-0 mb-2">{versionLabel}</p>
            {product?.description ? (
              <p className="text-sm leading-normal break-words mt-0 mb-6">{product.description}</p>
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h3 className="font-normal text-[1.375rem] leading-7 m-0 text-primary">Accessibility Conformance Report</h3>
              {acrExportReady ? (
                <AcrDownloadMenu
                  evaluation={publicAcrExport.evaluation}
                  catalog={publicAcrExport.catalog}
                  enabled={Boolean(publicAcrExport.catalog?.chapters?.length)}
                  menuButtonId="product-detail-acr-download-button"
                  menuId="product-detail-acr-download-menu"
                />
              ) : null}
            </div>

            <AcrReportViewer
              productName={product?.name}
              exportState={publicAcrExport}
              fallback={acrViewerFallback}
            />
          </div>

          <div className="col-span-12 lg:col-span-4 flex flex-col gap-6">
            <div className="px-8 py-4 rounded-sm bg-accent-cool-lighter/30">
              <h3 className="text-[1.375rem] leading-7 font-normal text-primary-darker inline-block mb-2 mt-0">Get In Touch</h3>
              {hasAnyProductPoc ? (
                <div>
                  {productPocPhone ? (
                    <div className="flex [&>span]:block [&>span:last-child]:flex-1">
                      <span className="text-accent-cool mr-4"><FontAwesomeIcon icon={faPhone} aria-hidden="true" /></span>
                      <span className="text-primary-darker"><span className="font-semibold">Phone: </span>{productPocPhone}</span>
                    </div>
                  ) : null}
                  {productPocEmail ? (
                    <div className="flex [&>span]:block [&>span:last-child]:flex-1">
                      <span className="text-accent-cool mr-4"><FontAwesomeIcon icon={faEnvelope} aria-hidden="true" /></span>
                      <span className="text-primary-darker"><span className="font-semibold">Email: </span>{productPocEmail}</span>
                    </div>
                  ) : null}
                  {productPocAddress ? (
                    <div className="flex [&>span]:block [&>span:last-child]:flex-1">
                      <span className="text-accent-cool mr-4"><FontAwesomeIcon icon={faLocationPin} aria-hidden="true" /></span>
                      <span className="text-primary-darker"><span className="font-semibold">Address: </span>{productPocAddress}</span>
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="text-primary-darker m-0">
                  Contact details not provided. Reach out to{" "}
                  <a href={`mailto:${ACR_REPO_ADMIN_EMAIL}`}>{ACR_REPO_ADMIN_EMAIL}</a> for more information.
                </p>
              )}
            </div>

            <div className="rounded-sm bg-accent-cool-lighter/30 p-5">
              <h4 className="font-bold text-primary mt-0 mb-4">{`${product?.name}'s ACR details`}</h4>
              <dl className="m-0">
                <AcrDetailField title="Product Category">{product?.category}</AcrDetailField>
                <AcrDetailField title="PSC">{product?.pscCode}</AcrDetailField>
                <AcrDetailField title="FedRAMP Status">{product?.fedRampStatus}</AcrDetailField>
                <AcrDetailField title="Vendor">{getVendorLink()}</AcrDetailField>
                <AcrDetailField title="Date ACR Added">{dateAcrAdded}</AcrDetailField>
                <AcrDetailField title="Date ACR Last Updated">{dateAcrUpdated}</AcrDetailField>
                {showExternalAcrFields ? (
                  <>
                    <AcrDetailField title="ACR Type">{getAcrTypeLabel(hyperlink?.acrType)}</AcrDetailField>
                    <AcrDetailField title="Product ACR">{getProductAcrLinkContent()}</AcrDetailField>
                  </>
                ) : null}
              </dl>
            </div>
          </div>
        </div>

        <p className="mt-8 mb-0 text-gray-dark">
          <small>Product last updated on {formatDisplayDate(product?.lastModifiedDate)} by {product?.lastModifiedBy} {displayUserType()}</small>
        </p>
      </SectionContainer>
    </>
  );
}

export default ProductDetailListContainerFC;

